import {
  SET_PROMOS_SEARCH,
  SET_PROMOS_ISSUE_FILTER,
  UPDATE_PROMOS,
  UPDATE_PROMOTION_DETAIL,
  SET_PROMOTION_DETAIL_LOADING,
  RESET_PROMOTION_DETAIL,
  SET_DISCOUNT_DETAILS_SEARCH,
  SET_PROMOTION_STAGES_LOADING,
  UPDATE_PROMOTION_EVENTS,
  SET_PROMOTION_EVENTS_LOADING,
  SET_PROMOTION_EVENTS_SEARCH,
  SET_PROMOTION_EVENTS_PAGINATION,
  UPDATE_PROMOTION_ISSUES,
  SET_PROMOTION_ISSUES_LOADING,
  UPDATE_PROMOTION_DELIVERIES,
  SET_PROMOTION_DELIVERIES_LOADING,
  OPEN_DISCOUNT_DETAILS_DRAWER,
  CLOSE_DISCOUNT_DETAILS_DRAWER,
  UPDATE_DISCOUNT_DETAILS,
  UPDATE_DISCOUNT_DETAILS_DATA,
  SET_DISCOUNT_DETAILS_LOADING,
  UPDATE_DISCOUNT_ACTIVE_STATUS,
  OPEN_CLIENT_DISCOUNT_DRAWER,
  CLOSE_CLIENT_DISCOUNT_DRAWER,
  SET_LIFECYCLE_FILTER,
  UPDATE_PROMOTION_OWNER,
  UPDATE_PROMOTION_ASSIGNEE,
  REVERT_PROMOTION_OWNER,
  REVERT_PROMOTION_ASSIGNEE,
  OPEN_DELIVERY_DRAWER,
  CLOSE_DELIVERY_DRAWER,
  UPDATE_DELIVERY_NOTES,
  SET_DELIVERY_NOTES_LOADING,
  UPDATE_GRID_PREFERENCES,
  REVERT_GRID_PREFERENCES,
  GRID_PREFERENCES_REQUESTED,
  GRID_PREFERENCES_LOADED,
  GRID_PREFERENCES_FAILED,
} from "./constants";
import { GRID_PREFERENCES_STATUS } from "ppt/constants";

const initialState = {
  promos: [],
  promosSearch: "",
  promosIssueFilter: null,
  promotionDetail: null,
  promotionDetailLoading: true,
  promotionStages: [],
  promotionStagesLoading: false,
  discountDetails: [],
  discountDetailsSearch: "",
  promotionEvents: [],
  promotionEventsLoading: true,
  promotionEventsSearch: "",
  promotionEventsPagination: {
    total: 0,
    offset: 0,
    limit: 50,
  },
  promotionIssues: [],
  promotionIssuesLoading: true,
  // Deliveries panel — derived read model from GET /promotions/{id}/deliveries
  promotionDeliveries: null,
  promotionDeliveriesLoading: true,
  totalIssueCount: 0,
  promotionIssuesData: null,
  discountDetailsDrawerOpen: false,
  selectedDiscountId: null,
  selectedDiscount: null,
  discountIssues: null,
  discountDetailsLoading: true,
  // ClientDiscount drawer (separate surface from the Discount drawer above)
  clientDiscountDrawerOpen: false,
  selectedClientDiscount: null,
  // Delivery drawer — shows delivery details and notes
  deliveryDrawerOpen: false,
  selectedDelivery: null,
  deliveryNotes: null,
  deliveryNotesLoading: true,
  // Discount-grid filter dispatched from the lifecycle widget's stat tiles.
  // null means no tile selected → grid shows everything.
  lifecycleFilter: null,
  // Per-user grid column preferences, fetched once per session (see
  // PPMPreferencesGate). Null until the fetch settles, which is why the status
  // below is what grids gate on — null alone can't distinguish "not here yet"
  // from "this user has saved nothing".
  gridPreferences: null,
  gridPreferencesStatus: GRID_PREFERENCES_STATUS.NOT_REQUESTED,
};

/**
 * Merges parent discount issues and client discount issues into a single combined object.
 * Both have the same structure: { issueCount, maxSeverity, byType: [...] }
 */
function mergeDiscountIssues(parentIssues, clientIssues, overallMaxSeverity) {
  if (!parentIssues && !clientIssues) return null;
  if (!parentIssues)
    return { ...clientIssues, maxSeverity: overallMaxSeverity };
  if (!clientIssues)
    return { ...parentIssues, maxSeverity: overallMaxSeverity };

  // Merge byType arrays: combine issues of the same type
  const byTypeMap = new Map();

  [...(parentIssues.byType || []), ...(clientIssues.byType || [])].forEach(
    (issueTypeGroup) => {
      const key = issueTypeGroup.issueType;
      if (!byTypeMap.has(key)) {
        byTypeMap.set(key, {
          issueType: issueTypeGroup.issueType,
          severity: issueTypeGroup.severity,
          summary: issueTypeGroup.summary,
          issues: [],
        });
      }
      const existing = byTypeMap.get(key);
      existing.issues.push(...issueTypeGroup.issues);
    },
  );

  return {
    issueCount: (parentIssues.issueCount || 0) + (clientIssues.issueCount || 0),
    maxSeverity: overallMaxSeverity,
    byType: Array.from(byTypeMap.values()),
  };
}

export function ppt(state = initialState, action) {
  switch (action.type) {
    case UPDATE_PROMOS:
      return {
        ...state,
        promos: action.payload,
      };
    case SET_PROMOS_SEARCH:
      return { ...state, promosSearch: action.payload };
    case SET_PROMOS_ISSUE_FILTER:
      return { ...state, promosIssueFilter: action.payload ?? null };
    case UPDATE_PROMOTION_DETAIL: {
      const { promotionDetail, stages } = action.payload || {};
      return {
        ...state,
        promotionDetail: action.payload ? promotionDetail : null,
        promotionStages: stages || [],
      };
    }
    case UPDATE_DISCOUNT_DETAILS_DATA: {
      const { discounts, discountCountByStatus } = action.payload || {};
      return {
        ...state,
        discountDetails: discounts || [],
        // Also update discountCountByStatus in promotionDetail if it exists
        promotionDetail: state.promotionDetail
          ? {
              ...state.promotionDetail,
              discountCountByStatus:
                discountCountByStatus ||
                state.promotionDetail.discountCountByStatus,
            }
          : null,
      };
    }
    case SET_PROMOTION_DETAIL_LOADING:
      return { ...state, promotionDetailLoading: action.payload };
    case SET_DISCOUNT_DETAILS_SEARCH:
      return { ...state, discountDetailsSearch: action.payload };
    case SET_PROMOTION_STAGES_LOADING:
      return { ...state, promotionStagesLoading: action.payload };
    case UPDATE_PROMOTION_EVENTS:
      return { ...state, promotionEvents: action.payload };
    case SET_PROMOTION_EVENTS_LOADING:
      return { ...state, promotionEventsLoading: action.payload };
    case SET_PROMOTION_EVENTS_SEARCH:
      return { ...state, promotionEventsSearch: action.payload };
    case SET_PROMOTION_EVENTS_PAGINATION:
      return { ...state, promotionEventsPagination: action.payload };
    case UPDATE_PROMOTION_ISSUES:
      return {
        ...state,
        promotionIssuesData: action.payload,
        totalIssueCount: action.payload?.totalIssues || 0,
      };
    case SET_PROMOTION_ISSUES_LOADING:
      return { ...state, promotionIssuesLoading: action.payload };
    case UPDATE_PROMOTION_DELIVERIES:
      return { ...state, promotionDeliveries: action.payload };
    case SET_PROMOTION_DELIVERIES_LOADING:
      return { ...state, promotionDeliveriesLoading: action.payload };
    case OPEN_DISCOUNT_DETAILS_DRAWER: {
      const discountId = action.payload;
      return {
        ...state,
        discountDetailsDrawerOpen: true,
        selectedDiscountId: discountId,
        selectedDiscount: null,
      };
    }
    case CLOSE_DISCOUNT_DETAILS_DRAWER:
      return {
        ...state,
        discountDetailsDrawerOpen: false,
      };
    case UPDATE_DISCOUNT_DETAILS: {
      const detailsData = action.payload;
      if (!detailsData) {
        return {
          ...state,
          selectedDiscount: null,
          discountIssues: null,
        };
      }
      // Destructure: issues, clientDiscountIssues, and overallMaxSeverity from response
      const {
        issues,
        clientDiscountIssues,
        overallMaxSeverity,
        ...discountFields
      } = detailsData;
      const mergedIssues = mergeDiscountIssues(
        issues,
        clientDiscountIssues,
        overallMaxSeverity,
      );
      return {
        ...state,
        selectedDiscount: discountFields,
        discountIssues: mergedIssues,
      };
    }
    case SET_DISCOUNT_DETAILS_LOADING:
      return { ...state, discountDetailsLoading: action.payload };
    case UPDATE_DISCOUNT_ACTIVE_STATUS: {
      const { updatedDiscount } = action.payload;

      // Update selectedDiscount for immediate feedback - parent will refetch promotion data
      return {
        ...state,
        selectedDiscount: updatedDiscount || state.selectedDiscount,
      };
    }
    case OPEN_CLIENT_DISCOUNT_DRAWER:
      return {
        ...state,
        clientDiscountDrawerOpen: true,
        selectedClientDiscount: action.payload,
      };
    case CLOSE_CLIENT_DISCOUNT_DRAWER:
      return {
        ...state,
        clientDiscountDrawerOpen: false,
      };
    case OPEN_DELIVERY_DRAWER:
      return {
        ...state,
        deliveryDrawerOpen: true,
        selectedDelivery: action.payload,
        deliveryNotes: null,
      };
    case CLOSE_DELIVERY_DRAWER:
      return {
        ...state,
        deliveryDrawerOpen: false,
        deliveryNotes: null,
        deliveryNotesLoading: true,
      };
    case UPDATE_DELIVERY_NOTES:
      return { ...state, deliveryNotes: action.payload };
    case SET_DELIVERY_NOTES_LOADING:
      return { ...state, deliveryNotesLoading: action.payload };
    case SET_LIFECYCLE_FILTER:
      return { ...state, lifecycleFilter: action.payload ?? null };
    case RESET_PROMOTION_DETAIL:
      return {
        ...state,
        promotionDetail: null,
        promotionDetailLoading: true,
        promotionStages: [],
        discountDetails: [],
        discountDetailsSearch: "",
        promotionEvents: [],
        promotionEventsLoading: true,
        promotionEventsSearch: "",
        promotionDeliveries: null,
        promotionDeliveriesLoading: true,
        discountDetailsDrawerOpen: false,
        selectedDiscountId: null,
        selectedDiscount: null,
        discountIssues: null,
        discountDetailsLoading: true,
        clientDiscountDrawerOpen: false,
        selectedClientDiscount: null,
        deliveryDrawerOpen: false,
        selectedDelivery: null,
        deliveryNotes: null,
        deliveryNotesLoading: true,
        lifecycleFilter: null,
      };
    case UPDATE_PROMOTION_OWNER:
      return {
        ...state,
        promotionDetail: {
          ...state.promotionDetail,
          owner: action.payload,
        },
      };
    case REVERT_PROMOTION_OWNER:
      return {
        ...state,
        promotionDetail: {
          ...state.promotionDetail,
          owner: action.payload,
        },
      };
    case UPDATE_PROMOTION_ASSIGNEE:
      return {
        ...state,
        promotionDetail: {
          ...state.promotionDetail,
          assignee: action.payload,
        },
      };
    case REVERT_PROMOTION_ASSIGNEE:
      return {
        ...state,
        promotionDetail: {
          ...state.promotionDetail,
          assignee: action.payload,
        },
      };
    case UPDATE_GRID_PREFERENCES:
      return {
        ...state,
        gridPreferences: {
          ...state.gridPreferences,
          ...action.payload,
        },
      };
    case GRID_PREFERENCES_REQUESTED:
      return {
        ...state,
        gridPreferencesStatus: GRID_PREFERENCES_STATUS.LOADING,
      };
    case GRID_PREFERENCES_LOADED:
      return {
        ...state,
        gridPreferences: action.payload || {},
        gridPreferencesStatus: GRID_PREFERENCES_STATUS.LOADED,
      };
    // A failed fetch settles exactly like a successful one that came back
    // empty, so grids stop waiting and fall back to their default layouts.
    case GRID_PREFERENCES_FAILED:
      return {
        ...state,
        gridPreferences: {},
        gridPreferencesStatus: GRID_PREFERENCES_STATUS.LOADED,
      };
    case REVERT_GRID_PREFERENCES:
      return {
        ...state,
        gridPreferences: action.payload,
      };
    default:
      return state;
  }
}
